/**
 * The cook's preferences, resolved from the store into the one shape every
 * generator reads. See `services/cookPreferences.ts` for what is hard, what is
 * soft, and why they used to disagree with each other.
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectAllergens,
  selectCustomAllergens,
  selectTasteAxes,
} from '../redux/userPreferencesSlice';
import { selectTasteProfile } from '../redux/feedbackSlice';
import { selectAllRecipeMetadata } from '../redux/recipeMetadataSlice';
import { selectActiveInventory } from '../redux/inventorySlice';
import { buildAllergenTerms } from '../services/allergenMatcher';
import { resolveCookPreferences, type CookPreferences } from '../services/cookPreferences';
import type { RootState } from '../redux/store';

const EMPTY: string[] = [];

export interface CookPreferenceOptions {
  /**
   * Drop the declared diets from the hard rules for this read.
   *
   * The Recettes screen offers the cook a removable chip for a diet they
   * declared once in Réglages — "a default, not a lock" — and lifting it must
   * go through the resolver rather than around it. Allergies, intolerances and
   * excluded ingredients are never liftable and are unaffected.
   */
  ignoreDeclaredDiets?: boolean;
}

export function useCookPreferences(options?: CookPreferenceOptions): CookPreferences {
  const ignoreDeclaredDiets = options?.ignoreDeclaredDiets ?? false;
  const axes = useSelector(selectTasteAxes);
  const tasteProfile = useSelector(selectTasteProfile);
  const allergens = useSelector(selectAllergens);
  const customAllergens = useSelector(selectCustomAllergens);
  const dislikedIngredients = useSelector(
    (s: RootState) => s.userPreferences?.dislikedIngredients ?? EMPTY,
  );
  const savedRestrictions = useSelector(
    (s: RootState) => s.userPreferences?.dietaryRestrictions ?? EMPTY,
  );
  const dietaryRestrictions = ignoreDeclaredDiets ? EMPTY : savedRestrictions;
  const metadataById = useSelector(selectAllRecipeMetadata);
  const inventory = useSelector(selectActiveInventory);

  const allergenTerms = useMemo(
    () => buildAllergenTerms(allergens, customAllergens),
    [allergens, customAllergens],
  );
  // The rows themselves, not their names: the score then shares the shelf's
  // matcher with the fan and side rankings (#865).
  return useMemo(
    () => resolveCookPreferences({
      axes,
      tasteProfile,
      allergenTerms,
      dislikedIngredients,
      dietaryRestrictions,
      metadataById,
      pantry: inventory,
    }),
    [axes, tasteProfile, allergenTerms, dislikedIngredients, dietaryRestrictions, metadataById, inventory],
  );
}
